/**
 * The television's end of the second screen: it obeys commands, and it says what it is doing.
 *
 * ## Why the TV reports rather than the phone asking
 *
 * A phone that polled for "what is playing" would be wrong for as long as its interval,
 * and would keep asking while nothing was on. Pushing from the side that knows means the
 * remote's now-playing view is correct within a frame of the picture changing, and silent
 * the rest of the time.
 *
 * The report is deliberately thin — item id, state, position — and the phone resolves the
 * title from the catalogue it already has. Sending the artwork and title down this channel
 * would mean the TV's idea of an item and the phone's could disagree, and the one on the
 * screen the viewer is not looking at would win.
 *
 * ## Position is throttled, state changes are not
 *
 * Play, pause and stop go out immediately: they are what the remote's button is waiting to
 * reflect, and a delayed one reads as a dropped press. Position goes out every few seconds,
 * because a progress bar that is three seconds stale is indistinguishable from a correct
 * one and a frame-accurate one would be a message per frame.
 */
import { useCallback, useEffect, useRef } from 'react';
import {
  REMOTE_TRACKS_MAX,
  REMOTE_TRACK_LABEL_MAX,
  type RemoteTracksDto,
} from '@filmodasi/shared';
import type { EngineTracks } from '../player/engines';
import type { PlayerActions } from '../layouts/tv/useTvPlayerKeys';
import type { ServerMessage } from './useRealtime';

/** Often enough for a progress bar, rare enough to be nothing on the wire. */
const POSITION_INTERVAL_MS = 5_000;
/** Matches the remote's ⏪/⏩ step, so both screens move by the same amount. */
const STEP_SEC = 10;

export type PlaybackState = 'playing' | 'paused' | 'stopped';

export interface TvPlaybackOptions {
  /** False everywhere but a television with a player on screen. */
  enabled: boolean;
  itemId: string | null;
  actions: PlayerActions;
  /** Read at call time — the engine is rebuilt on a re-mint. */
  currentTime: () => number;
  durationSec: () => number;
  paused: () => boolean;
  /** Sends on the household socket. Returns false when it is not open. */
  send: (payload: Record<string, unknown>) => boolean;
  /** Registers a listener for commands arriving from a phone. */
  onMessage: (handler: (message: ServerMessage) => void) => () => void;
  /** Back and Home are navigation, which this hook does not own. */
  onNavigate: (where: 'back' | 'home') => void;
  /**
   * The attached engine's track interface, as a getter, or null where it has none.
   *
   * A getter for the same reason as everything else here: the engine is rebuilt whenever a
   * session is re-minted, and a captured reference would answer for a player that has been
   * torn down.
   */
  tracks?: () => EngineTracks | null;
}

/**
 * The track list, reduced to what a phone needs.
 *
 * Returns null — not an empty list — where there is nothing to offer, so the phone can
 * tell "this engine cannot enumerate tracks" from "it can, and there is one of each".
 * Those deserve different sentences, and an empty array would collapse them into one.
 */
function publishable(tracks: EngineTracks | null): RemoteTracksDto | null {
  if (!tracks) return null;
  const set = tracks.get();
  // A single audio track with no subtitles is not a choice. Publishing it would put a menu
  // on the phone whose only row is already selected.
  if (set.audio.length < 2 && set.subtitle.length === 0) return null;
  const strip = (list: readonly { id: string; label: string }[]) =>
    list.slice(0, REMOTE_TRACKS_MAX).map((t) => ({
      id: t.id,
      // Trimmed here rather than trusted: the server refuses an over-long label outright,
      // and losing the whole state message — position, duration, play state — because one
      // track came out of a provider stream with a pathological name would be a poor trade.
      label: t.label.slice(0, REMOTE_TRACK_LABEL_MAX),
    }));
  return {
    audio: strip(set.audio),
    subtitle: strip(set.subtitle),
    activeAudio: set.activeAudio,
    activeSubtitle: set.activeSubtitle,
  };
}

export function useTvPlayback(options: TvPlaybackOptions): void {
  // One ref, read at call time, so neither effect below re-binds when the player changes
  // underneath them — a re-mint would otherwise drop commands during the gap.
  const latest = useRef(options);
  latest.current = options;

  // Destructured rather than read as `options.enabled` inside the effects below.
  // `exhaustive-deps` cannot track member expressions on a plain local object, so it asked
  // for `options` itself — a new literal from the caller every render, which would have
  // re-subscribed the command socket on each one.
  const { enabled, itemId, onMessage } = options;

  /* -- report -- */
  //
  // Declared before the command effect that calls it, and memoised with an empty list: it
  // reads everything through `latest`, so it has no dependencies of its own. As a plain
  // arrow further down the file it was a fresh closure each render that both effects
  // captured once, which happened to work and left `exhaustive-deps` unable to say so.
  const report = useCallback(() => {
    const { itemId, send, paused, currentTime, durationSec, tracks } = latest.current;
    send({
      type: 'state',
      itemId,
      playbackState: itemId === null ? 'stopped' : paused() ? 'paused' : 'playing',
      positionSec: Math.floor(currentTime()),
      durationSec: Math.floor(durationSec()),
      tracks: publishable(tracks?.() ?? null),
    });
  }, []);

  /* -- obey -- */
  useEffect(() => {
    if (!enabled) return;

    return onMessage((message) => {
      if (message.type !== 'command') return;
      const { actions, onNavigate } = latest.current;

      switch (message.command) {
        case 'play':
          actions.play();
          break;
        case 'pause':
          actions.pause();
          break;
        case 'stop':
          // Stop on a television means "put the picture away", which is a navigation, not
          // a player state — leaving a paused frame on screen is not what the button says.
          actions.pause();
          onNavigate('back');
          break;
        case 'seek':
          // A number means an absolute position; its absence means the viewer tapped the
          // skip button rather than dragging the bar.
          if (typeof message.value === 'number') actions.seekTo(message.value);
          else actions.jump(STEP_SEC);
          break;
        case 'next':
          actions.jump(STEP_SEC);
          break;
        case 'previous':
          actions.jump(-STEP_SEC);
          break;
        case 'back':
          onNavigate('back');
          break;
        case 'home':
          onNavigate('home');
          break;
        case 'audioTrack':
        case 'subtitleTrack': {
          /*
           * The id is passed to the engine unchecked *by this hook* and checked by the
           * engine, which ignores anything not in its own list. That is the point of
           * sending an id rather than an ordinal: the phone's list can go stale — a
           * re-mint rebuilds the engine mid-film — and a stale id then selects nothing
           * instead of selecting whatever now happens to sit at that position.
           */
          const engineTracks = latest.current.tracks?.() ?? null;
          if (!engineTracks) break;
          const kind = message.command === 'audioTrack' ? 'audio' : 'subtitle';
          // Absent means off, which only subtitles have. An audio command with no track
          // names nothing, so there is nothing to do rather than something to guess.
          const id = message.trackId ?? null;
          if (kind === 'audio' && id === null) break;
          engineTracks.select(kind, id);
          break;
        }
        default:
          /*
           * Volume is accepted by the server and deliberately not acted on here. Tizen
           * exposes it through `tizen.tvaudiocontrol`, which the widget has no privilege
           * for in this build, and doing nothing visible is better than a remote button
           * that appears to work and does not.
           *
           * `void` rather than a silent fallthrough so this reads as a decision.
           */
          void message.command;
          break;
      }

      // Whatever just happened, the phone should see it rather than wait for the tick.
      report();
    });
  }, [enabled, onMessage, report]);

  useEffect(() => {
    if (!enabled) return;

    report();
    const timer = window.setInterval(report, POSITION_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
      /*
       * One last message on the way out, so a phone showing "playing" does not keep
       * showing it after the viewer left the player. Without this the remote's only clue
       * would be the position bar failing to advance, which looks like a stall rather
       * than a stop.
       */
      latest.current.send({ type: 'state', itemId: null, playbackState: 'stopped' });
    };
    // `itemId` is a dependency so switching films re-reports immediately.
  }, [enabled, itemId, report]);
}
